/* eslint-disable eqeqeq */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BsChevronDown, BsChevronUp, BsSliders } from "react-icons/bs";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";
import { Box, Flex } from "rebass";
import ChevronDown from "../../icons/ChevronDown";
import { getLocalizedString } from "../../utils";
import { getFilterToChips, rmvFilter } from "../../utils/filterOrderToChips";
import "../styles.css";
import { SeasonBlock } from "./components/SeasonBlock";
import useFiltersData from "../../hooks/useFiltersData";
import { LineBlock } from "./components/LineBlock";
import useResponsive from "../../hooks/useResponsive";
import FiltersAndSearchMobile from "../common/FiltersAndSearchMobile";
import useFiltersAndSearchMobile from "../../hooks/useFiltersAndSearchMobile";
import OrderStatusBadge from "./OrderStatusBadge";
import { theme } from "../../utils";
import getOrderStatusCount from "../../utils/getOrderStatusCount";
import { ORDER_STATUS } from "../../model/order";

const OrderFilter = ({ filters, setFilters, lineFromCustomerConfig }) => {
  const lang = localStorage.getItem("B2BLanguage") == "ITA" ? "it" : "en";
  const { orders } = useSelector((state) => state.yodaOrders);
  const { configuration } = useSelector((state) => state.yodaOrders);

  const { listFilters } = useFiltersData();
  const [showFilters, setShowFilters] = useState(false);
  const [showStatusOptions, setShowStatusOptions] = useState(false);
  const [showTypeOptions, setShowTypeOptions] = useState(false);
  const [showSeasonOptions, setShowSeasonOptions] = useState(false);
  const [showLineOptions, setShowLineOptions] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [lineOptions, setLineOptions] = useState([]);
  const [statusOptions, setStatusOption] = useState([]);
  const [chips, setChips] = useState([null, null, null, null]);

  const typeRef = useRef();
  const statusRef = useRef();
  const seasonRef = useRef();
  const lineRef = useRef();

  const { isMobile, isTabletSm } = useResponsive();
  const filterProps = useFiltersAndSearchMobile();

  const [hasFilter, setHasFilter] = useState(false);

  const { draftCount, confirmedCount, cancelledCount, sentCount } =
    useMemo(() => {
      return getOrderStatusCount(orders);
    }, [orders]);

  useEffect(() => {
    setHasFilter(false);
    chips.forEach((c) => {
      if (c) {
        setHasFilter(true);
        return;
      }
    });
  }, [chips]);

  useEffect(() => {
    const chips = getFilterToChips(filters, listFilters);
    setChips(chips);
  }, [filters, lang]);

  useEffect(() => {
    if (orders && orders.length > 0) {
      let t = [];
      t = [
        { value: "all", label: lang == "en" ? "All type" : "Tutti i tipi" },
        ...[
          ...new Map(
            orders.map((orderDetailsFiltered) => [
              orderDetailsFiltered?.typeId,
              {
                value: orderDetailsFiltered?.typeId,
                label: orderDetailsFiltered?.type?.[lang] || "",
              },
            ]),
          ).values(),
        ],
      ];
      setTypeOptions(t);

      let s = [];
      s = [
        {
          value: "all",
          label: lang == "en" ? "All seasons" : "Tutte le stagioni",
        },
        ...[
          ...new Map(
            orders.map((order) => [
              order?.seasonId,
              { value: order?.seasonId, label: order?.season?.[lang] || "" },
            ]),
          ).values(),
        ],
      ];
      setSeasonOptions(s);

      let l = [];
      if (lineFromCustomerConfig) {
        l = [
          {
            value: "all",
            label: lang == "en" ? "All lines" : "Tutte le linee",
          },
          ...[
            ...new Map(
              orders.map((o) => [
                o?.customData?.lineId || o?.customdata?.lineId,
                {
                  value: o?.customData?.lineId || o?.customdata?.lineId,
                  label:
                    (!!configuration?.length &&
                      configuration?.[0].configuration.find(
                        (l) =>
                          l?.lineID == o?.customData?.lineId ||
                          l?.lineID == o?.customdata?.lineId,
                      )?.lineDescription?.[lang]) ||
                    "missing",
                },
              ]),
            ).values(),
          ],
        ];
      } else {
        l = [
          {
            value: "all",
            label: lang == "en" ? "All lines" : "Tutte le linee",
          },
          ...[
            ...new Map(
              orders.map((order) => [
                order?.line,
                {
                  value: order?.line,
                  label: order?.lineDescription?.[lang] || "",
                },
              ]),
            ).values(),
          ],
        ];
      }
      setLineOptions(l);

      const st = [
        {
          value: 100,
          label: lang == "en" ? "All statuses" : "Tutti gli stati",
        },
        {
          value: ORDER_STATUS.DRAFT,
          label: getLocalizedString("STATUS_C_DRAFT"),
        },
        {
          value: ORDER_STATUS.SUBMITTED,
          label: getLocalizedString("STATUS_C_WAITINGFORCONFIRMATION"),
        },
        {
          value: ORDER_STATUS.CONFIRMED,
          label: getLocalizedString("STATUS_C_CONFIRMED"),
        },
        {
          value: ORDER_STATUS.CANCELLED,
          label: getLocalizedString("STATUS_C_CANCELLED"),
        },
      ];
      setStatusOption(st);

      if (!isMobile && !isTabletSm) {
        // in case of mobile the refs (typeRef, statusRef, ...) are not present, so skip this
        if (filters["typeId"] !== "-") {
          let tmpValue = t.filter((f) => f.value === filters["typeId"]);
          if (tmpValue.length > 0) {
            typeRef.current.controlRef.childNodes[0].childNodes[0].childNodes[0].nodeValue =
              tmpValue[0]?.label;
          }
        }

        if (filters["status"] !== "-") {
          let tmpValue = st.filter((f) => f.value === filters["status"]);
          if (tmpValue.length > 0) {
            statusRef.current.controlRef.childNodes[0].childNodes[0].childNodes[0].nodeValue =
              tmpValue[0]?.label;
          }
        }

        if (filters["season"] !== "-") {
          let tmpValue = s.filter((f) => f.value === filters["season"]);
          if (tmpValue.length > 0) {
            seasonRef.current.controlRef.childNodes[0].childNodes[0].childNodes[0].nodeValue =
              tmpValue[0].label;
          }
        }

        if (filters["line"] !== "-") {
          let tmpValue = l.filter((f) => f.value === filters["line"]);
          if (tmpValue.length > 0) {
            lineRef.current.controlRef.childNodes[0].childNodes[0].childNodes[0].nodeValue =
              tmpValue[0].label;
          }
        }
      }
    }
  }, [orders, filters, lang]);

  let selectStyle = {
    control: (styles) => ({
      ...styles,
      border: 0,
      borderRadius: 0,
      backgroundColor: "white",
      fontSize: "0.7em",
      marginTop: 0,
      boxShadow: "none",
    }),
    menu: (styles) => ({
      ...styles,
      padding: 0,
      borderRadius: 0,
      fontSize: "0.8em",
      marginTop: 0,
      marginBottom: 0,
      boxShadow: "none",
      border: "1px solid rgba(0,0,0,.2)",
    }),
    option: (styles) => ({
      ...styles,
      marginTop: 1,
      marginBottom: 1,
    }),
  };

  function changeFilters(filterKey, filterValue) {
    if (filters[filterKey] !== filterValue) {
      setFilters({
        ...filters,
        [filterKey]: filterValue,
      });
    }
  }

  function removeFilter(filterCode) {
    const newFilters = rmvFilter(filterCode, filters);
    setFilters(newFilters);
  }

  function selectFilterStatus(opt) {
    changeFilters("status", opt.value);
    setShowStatusOptions(false);
  }
  function selectFilterTypeOptions(opt) {
    changeFilters("typeId", opt.value);
    setShowTypeOptions(false);
  }

  function selectFilterSeasonOptions(opt) {
    changeFilters("season", opt.value);
    setShowSeasonOptions(false);
  }

  function selectFilterLineOptions(opt) {
    changeFilters("line", opt.value);
    setShowLineOptions(false);
  }

  function openStatus() {
    setShowStatusOptions(!showStatusOptions);
    setShowSeasonOptions(false);
    setShowLineOptions(false);
    setShowTypeOptions(false);
  }

  function openSeason() {
    setShowSeasonOptions(!showSeasonOptions);
    setShowStatusOptions(false);
    setShowLineOptions(false);
    setShowTypeOptions(false);
  }

  function openLine() {
    setShowLineOptions(!showLineOptions);
    setShowStatusOptions(false);
    setShowSeasonOptions(false);
    setShowTypeOptions(false);
  }

  function openType() {
    setShowTypeOptions(!showTypeOptions);
    setShowStatusOptions(false);
    setShowSeasonOptions(false);
    setShowLineOptions(false);
  }

  function removeAllFilters() {
    setFilters({
      status: "-",
      typeId: "-",
      line: "-",
      season: "-",
    });
    setHasFilter(false);
  }

  return !isMobile && !isTabletSm ? (
    <Flex
      width={[null, 1, 1]}
      mb={0}
      style={{
        backgroundColor: "#f5f5f5",
      }}
      justifyContent="space-evenly"
      mt={[null, 30, 30]}
      flexDirection={["column", "unset", "row"]}
      flexWrap={["nowrap", "wrap", "nowrap"]}
    >
      <Box width={[1, 1 / 2, 1 / 4]} m={[0, null, 12]} p={[3, null, 0]}>
        <Select
          ref={statusRef}
          styles={selectStyle}
          options={statusOptions}
          placeholder={getLocalizedString("Stato").toUpperCase()}
          onChange={(e) => changeFilters("status", e.value)}
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: (props) => (
              <components.DropdownIndicator {...props}>
                <ChevronDown
                  style={{ width: 20, height: 20, color: "#3e3e3e" }}
                />
              </components.DropdownIndicator>
            ),
          }}
          isSearchable={false}
        />
      </Box>
      <Box width={[1, 1 / 2, 1 / 4]} m={[0, null, 12]} p={[3, null, 0]}>
        <Select
          ref={typeRef}
          styles={selectStyle}
          options={typeOptions}
          onChange={(e) => changeFilters("typeId", e.value)}
          placeholder={getLocalizedString("Tipo").toUpperCase()}
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: (props) => (
              <components.DropdownIndicator {...props}>
                <ChevronDown
                  style={{ width: 20, height: 20, color: "#3e3e3e" }}
                />
              </components.DropdownIndicator>
            ),
          }}
          isSearchable={false}
        />
      </Box>
      <Box width={[1, 1 / 2, 1 / 4]} m={[0, null, 12]} p={[3, null, 0]}>
        <Select
          ref={seasonRef}
          styles={selectStyle}
          options={seasonOptions}
          onChange={(e) => changeFilters("season", e.value)}
          placeholder={getLocalizedString("Stagione").toUpperCase()}
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: (props) => (
              <components.DropdownIndicator {...props}>
                <ChevronDown
                  style={{ width: 20, height: 20, color: "#3e3e3e" }}
                />
              </components.DropdownIndicator>
            ),
          }}
          isSearchable={false}
        />
      </Box>
      <Box width={[1, 1 / 2, 1 / 4]} m={[0, null, 12]} p={[3, null, 0]}>
        <Select
          ref={lineRef}
          styles={selectStyle}
          options={lineOptions}
          onChange={(e) => changeFilters("line", e.value)}
          placeholder={getLocalizedString("Linea").toUpperCase()}
          components={{
            IndicatorSeparator: () => null,
            DropdownIndicator: (props) => (
              <components.DropdownIndicator {...props}>
                <ChevronDown
                  style={{ width: 20, height: 20, color: "#3e3e3e" }}
                />
              </components.DropdownIndicator>
            ),
          }}
          isSearchable={false}
        />
      </Box>
    </Flex>
  ) : (
    <>
      <FiltersAndSearchMobile
        activeFilters={chips}
        isFiltered={hasFilter}
        onRemoveAllFilters={removeAllFilters}
        {...{ removeFilter }}
        {...filterProps}
      >
        <Flex
          bg="#DEDEDE"
          height="40px"
          width="100%"
          style={{ borderBottom: "2px solid #d2d2d2" }}
          px="10px"
          alignItems="center"
          justifyContent="space-between"
          onClick={openStatus}
        >
          <span>{getLocalizedString("Stato").toUpperCase()}</span>
          <div>{showStatusOptions ? <BsChevronUp /> : <BsChevronDown />}</div>
        </Flex>
        <Flex flexDirection="column">
          {showStatusOptions &&
            statusOptions.map((opt, i) => (
              <Flex
                key={i}
                height="40px"
                px="10px"
                alignItems="center"
                bg="#f2f2f2"
                mb="2px"
                onClick={() => selectFilterStatus(opt)}
              >
                <span>{opt.label}</span>
              </Flex>
            ))}
        </Flex>
        <Flex
          bg="#DEDEDE"
          height="40px"
          width="100%"
          style={{ borderBottom: "2px solid #d2d2d2" }}
          px="10px"
          alignItems="center"
          justifyContent="space-between"
          onClick={openType}
        >
          <span>{getLocalizedString("Tipo").toUpperCase()}</span>
          <div>{showTypeOptions ? <BsChevronUp /> : <BsChevronDown />}</div>
        </Flex>
        <Flex flexDirection="column">
          {showTypeOptions &&
            typeOptions.map((opt, i) => (
              <Flex
                key={i}
                height="40px"
                px="10px"
                alignItems="center"
                bg="#f2f2f2"
                mb="2px"
                onClick={() => selectFilterTypeOptions(opt)}
              >
                <span>{opt.label}</span>
              </Flex>
            ))}
        </Flex>
        <SeasonBlock
          showSeasonOptions={showSeasonOptions}
          seasonOptions={seasonOptions}
          openSeason={openSeason}
          selectFilterSeasonOptions={selectFilterSeasonOptions}
        />
        <LineBlock
          openLine={openLine}
          showLineOptions={showLineOptions}
          lineOptions={lineOptions}
          selectFilterLineOptions={selectFilterLineOptions}
        />
      </FiltersAndSearchMobile>
      <Box>
        <Flex
          sx={{
            gap: "4px",
            flexWrap: "wrap",
            justifyContent: ["center", "center", "flex-start"],
            paddingTop: ["10px", "20px", 0],
          }}
        >
          <OrderStatusBadge
            color={theme.draftColor}
            label={getLocalizedString(
              draftCount === 1 ? "RECAP_DRAFT_SINGULAR" : "RECAP_DRAFT",
            )}
            count={draftCount}
            onClick={() =>
              selectFilterStatus({
                value: 0,
                label: getLocalizedString(
                  draftCount === 1 ? "RECAP_DRAFT_SINGULAR" : "RECAP_DRAFT",
                ),
              })
            }
            style={{ cursor: "pointer" }}
          />
          <OrderStatusBadge
            color={theme.confirmedColor}
            label={getLocalizedString(
              confirmedCount === 1
                ? "Recap draft confirmed/singular"
                : "Recap draft confirmed/plural",
            )}
            count={confirmedCount}
            onClick={() =>
              selectFilterStatus({
                value: 30,
                label: getLocalizedString(
                  confirmedCount === 1
                    ? "Recap draft confirmed/singular"
                    : "Recap draft confirmed/plural",
                ),
              })
            }
            style={{ cursor: "pointer" }}
          />
          <OrderStatusBadge
            color={theme.cancelledColor}
            label={getLocalizedString(
              cancelledCount === 1
                ? "Recap draft cancelled/singular"
                : "Recap draft cancelled/plural",
            )}
            count={cancelledCount}
            onClick={() =>
              selectFilterStatus({
                value: ORDER_STATUS.CANCELLED,
                label: getLocalizedString(
                  cancelledCount === 1
                    ? "Recap draft cancelled/singular"
                    : "Recap draft cancelled/plural",
                ),
              })
            }
            style={{ cursor: "pointer" }}
          />
          <OrderStatusBadge
            color={theme.waitingColor}
            label={getLocalizedString(
              sentCount === 1
                ? "Recap draft waiting/singular"
                : "Recap draft waiting/plural",
            )}
            count={sentCount}
            onClick={() =>
              selectFilterStatus({
                value: 10,
                label: getLocalizedString(
                  sentCount === 1
                    ? "Recap draft waiting/singular"
                    : "Recap draft waiting/plural",
                ),
              })
            }
            style={{ cursor: "pointer" }}
          />
        </Flex>
      </Box>
    </>
  );
};

export { OrderFilter };
